import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertiesService, Property } from '../services/properties.service';
import toast from 'react-hot-toast';
import { FiSave, FiX, FiArrowLeft } from 'react-icons/fi';
import { tw, monoFont } from '../utils/terminalTheme';

export const PropertyEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    property_type: '',
    bedrooms: '',
    bathrooms: '',
    square_feet: '',
    monthly_rent: '',
    occupancy_status: '',
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    lease_start_date: '',
    lease_end_date: '',
    hoa_url: '',
    hoa_login_url: '',
    hoa_username: '',
    hoa_password: '',
    hoa_notes: '',
    hoa_monthly_fee: '',
    // Payment schedule fields
    payment_bank_account: '',
    payment_frequency: '',
    payment_schedule_start_date: '',
    next_payment_date: '',
    payment_details: '',
    is_active: true,
    // Mortgage fields
    ownership_type: '',
    purchase_price: '',
    purchase_date: '',
    mortgage_lender: '',
    mortgage_lender_url: '',
    mortgage_account_number: '',
    mortgage_balance: '',
    mortgage_payment: '',
    mortgage_rate: '',
    mortgage_start_date: '',
    mortgage_maturity_date: ''
  });

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    try {
      setIsLoading(true);
      const propertyData = await propertiesService.getProperty(Number(id));
      setProperty(propertyData);

      // Populate form data
      setFormData({
        name: propertyData.name || '',
        address: propertyData.address || '',
        city: propertyData.city || '',
        state: propertyData.state || '',
        zip_code: propertyData.zip_code || '',
        property_type: propertyData.property_type || '',
        bedrooms: propertyData.bedrooms?.toString() || '',
        bathrooms: propertyData.bathrooms?.toString() || '',
        square_feet: propertyData.square_feet?.toString() || '',
        monthly_rent: propertyData.monthly_rent?.toString() || '',
        occupancy_status: propertyData.occupancy_status || '',
        tenant_name: propertyData.tenant_name || '',
        tenant_email: propertyData.tenant_email || '',
        tenant_phone: propertyData.tenant_phone || '',
        lease_start_date: propertyData.lease_start_date || '',
        lease_end_date: propertyData.lease_end_date || '',
        hoa_url: propertyData.hoa_url || '',
        hoa_login_url: propertyData.hoa_login_url || '',
        hoa_username: propertyData.hoa_username || '',
        hoa_password: propertyData.hoa_password || '',
        hoa_notes: propertyData.hoa_notes || '',
        hoa_monthly_fee: propertyData.hoa_monthly_fee?.toString() || '',
        // Payment schedule fields
        payment_bank_account: propertyData.payment_bank_account || '',
        payment_frequency: propertyData.payment_frequency || '',
        payment_schedule_start_date: propertyData.payment_schedule_start_date || '',
        next_payment_date: propertyData.next_payment_date || '',
        payment_details: propertyData.payment_details || '',
        is_active: propertyData.is_active,
        // Mortgage fields
        ownership_type: propertyData.ownership_type || '',
        purchase_price: propertyData.purchase_price?.toString() || '',
        purchase_date: propertyData.purchase_date || '',
        mortgage_lender: propertyData.mortgage_lender || '',
        mortgage_lender_url: propertyData.mortgage_lender_url || '',
        mortgage_account_number: propertyData.mortgage_account_number || '',
        mortgage_balance: propertyData.mortgage_balance?.toString() || '',
        mortgage_payment: propertyData.mortgage_payment?.toString() || '',
        mortgage_rate: propertyData.mortgage_rate?.toString() || '',
        mortgage_start_date: propertyData.mortgage_start_date || '',
        mortgage_maturity_date: propertyData.mortgage_maturity_date || ''
      });
    } catch (error: any) {
      console.error('Error loading property:', error);
      toast.error('Failed to load property');
      navigate('/properties');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Property name is required');
      return;
    }

    try {
      setIsSaving(true);

      // Convert string numbers back to numbers for API
      // Use undefined instead of null to match CreatePropertyDto
      const updateData = {
        name: formData.name.trim(),
        address: formData.address.trim() || undefined,
        city: formData.city.trim() || undefined,
        state: formData.state.trim() || undefined,
        zip_code: formData.zip_code.trim() || undefined,
        property_type: formData.property_type.trim() || undefined,
        bedrooms: formData.bedrooms ? Number(formData.bedrooms) : undefined,
        bathrooms: formData.bathrooms ? Number(formData.bathrooms) : undefined,
        square_feet: formData.square_feet ? Number(formData.square_feet) : undefined,
        monthly_rent: formData.monthly_rent ? Number(formData.monthly_rent) : undefined,
        occupancy_status: formData.occupancy_status.trim() || undefined,
        tenant_name: formData.tenant_name.trim() || undefined,
        tenant_email: formData.tenant_email.trim() || undefined,
        tenant_phone: formData.tenant_phone.trim() || undefined,
        lease_start_date: formData.lease_start_date || undefined,
        lease_end_date: formData.lease_end_date || undefined,
        hoa_url: formData.hoa_url.trim() || undefined,
        hoa_login_url: formData.hoa_login_url.trim() || undefined,
        hoa_username: formData.hoa_username.trim() || undefined,
        hoa_password: formData.hoa_password.trim() || undefined,
        hoa_notes: formData.hoa_notes.trim() || undefined,
        hoa_monthly_fee: formData.hoa_monthly_fee ? Number(formData.hoa_monthly_fee) : undefined,
        // Payment schedule fields
        payment_bank_account: formData.payment_bank_account.trim() || undefined,
        payment_frequency: formData.payment_frequency.trim() || undefined,
        payment_schedule_start_date: formData.payment_schedule_start_date || undefined,
        next_payment_date: formData.next_payment_date || undefined,
        payment_details: formData.payment_details.trim() || undefined,
        is_active: formData.is_active,
        // Mortgage fields
        ownership_type: formData.ownership_type.trim() || undefined,
        purchase_price: formData.purchase_price ? Number(formData.purchase_price) : undefined,
        purchase_date: formData.purchase_date || undefined,
        mortgage_lender: formData.mortgage_lender.trim() || undefined,
        mortgage_lender_url: formData.mortgage_lender_url.trim() || undefined,
        mortgage_account_number: formData.mortgage_account_number.trim() || undefined,
        mortgage_balance: formData.mortgage_balance ? Number(formData.mortgage_balance) : undefined,
        mortgage_payment: formData.mortgage_payment ? Number(formData.mortgage_payment) : undefined,
        mortgage_rate: formData.mortgage_rate ? Number(formData.mortgage_rate) : undefined,
        mortgage_start_date: formData.mortgage_start_date || undefined,
        mortgage_maturity_date: formData.mortgage_maturity_date || undefined
      };

      await propertiesService.updateProperty(Number(id), updateData);
      toast.success('Property updated successfully');
      navigate('/properties');
    } catch (error: any) {
      console.error('Error updating property:', error);
      toast.error(error.response?.data?.detail || 'Failed to update property');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className={tw.loadingContainer}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white font-mono mb-4">Property not found</h2>
          <button
            onClick={() => navigate('/properties')}
            className="text-cyan-400 hover:text-cyan-300 font-mono text-sm transition-colors"
          >
            Return to Properties
          </button>
        </div>
      </div>
    );
  }

  const propertySlug = (property.name || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1" style={monoFont}>
          <span className="text-cyan-500">~/</span>
          <span className="text-gray-400">properties/{propertySlug}/edit</span>
        </div>
        <div className="flex items-center gap-2" style={monoFont}>
          <span className="text-green-500">$</span>
          <span className="text-gray-500 text-sm">editing property record...</span>
        </div>
      </div>

      {/* Header Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/properties')}
              className="mr-4 p-2 text-gray-500 hover:text-cyan-400 rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white font-mono">Edit Property</h1>
              <p className="text-gray-500 text-sm font-mono mt-1">{property.name}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => navigate('/properties')}
              className={tw.btnSecondary + ' flex items-center'}
            >
              <FiX className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className={tw.btnPrimary + ' flex items-center disabled:opacity-50'}
            >
              <FiSave className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className={tw.formWrap}>
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-6">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={tw.label}>
                Property Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Property Type
              </label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleInputChange}
                className={tw.select}
              >
                <option value="">Select Type</option>
                <option value="single-family">Single Family</option>
                <option value="townhouse">Townhouse</option>
                <option value="condo">Condo</option>
                <option value="apartment">Apartment</option>
                <option value="duplex">Duplex</option>
                <option value="multi-family">Multi-Family</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className={tw.label}>
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                ZIP Code
              </label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Status
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-700 bg-gray-900 rounded"
                />
                <label className="ml-2 text-sm text-gray-400 font-mono">
                  Active Property
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div className={tw.formWrap}>
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-6">Property Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={tw.label}>
                Bedrooms
              </label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleInputChange}
                min="0"
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Bathrooms
              </label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleInputChange}
                min="0"
                step="0.5"
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Square Feet
              </label>
              <input
                type="number"
                name="square_feet"
                value={formData.square_feet}
                onChange={handleInputChange}
                min="0"
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Monthly Rent
              </label>
              <input
                type="number"
                name="monthly_rent"
                value={formData.monthly_rent}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Occupancy Status
              </label>
              <select
                name="occupancy_status"
                value={formData.occupancy_status}
                onChange={handleInputChange}
                className={tw.select}
              >
                <option value="">Select Status</option>
                <option value="occupied">Occupied</option>
                <option value="vacant">Vacant</option>
                <option value="maintenance">Maintenance</option>
                <option value="listed">Listed</option>
              </select>
            </div>

            <div>
              <label className={tw.label}>
                HOA Monthly Fee
              </label>
              <input
                type="number"
                name="hoa_monthly_fee"
                value={formData.hoa_monthly_fee}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={tw.input}
              />
            </div>
          </div>
        </div>

        {/* Tenant Information */}
        <div className={tw.formWrap}>
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-6">Tenant Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={tw.label}>
                Tenant Name
              </label>
              <input
                type="text"
                name="tenant_name"
                value={formData.tenant_name}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Tenant Email
              </label>
              <input
                type="email"
                name="tenant_email"
                value={formData.tenant_email}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Tenant Phone
              </label>
              <input
                type="tel"
                name="tenant_phone"
                value={formData.tenant_phone}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Lease Start Date
              </label>
              <input
                type="date"
                name="lease_start_date"
                value={formData.lease_start_date}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div className="md:col-span-2">
              <label className={tw.label}>
                Lease End Date
              </label>
              <input
                type="date"
                name="lease_end_date"
                value={formData.lease_end_date}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className={tw.formWrap}>
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-6">Financial Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={tw.label}>
                Ownership Type
              </label>
              <select
                name="ownership_type"
                value={formData.ownership_type}
                onChange={handleInputChange}
                className={tw.select}
              >
                <option value="">Select Type</option>
                <option value="owned_outright">Owned Outright</option>
                <option value="mortgaged">Mortgaged</option>
                <option value="contract_for_deed">Contract for Deed</option>
              </select>
            </div>

            <div>
              <label className={tw.label}>
                Purchase Price
              </label>
              <input
                type="number"
                name="purchase_price"
                value={formData.purchase_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Purchase Date
              </label>
              <input
                type="date"
                name="purchase_date"
                value={formData.purchase_date}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            {formData.ownership_type === 'mortgaged' && (
              <>
                <div>
                  <label className={tw.label}>
                    Mortgage Lender
                  </label>
                  <input
                    type="text"
                    name="mortgage_lender"
                    value={formData.mortgage_lender}
                    onChange={handleInputChange}
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Lender Website
                  </label>
                  <input
                    type="url"
                    name="mortgage_lender_url"
                    value={formData.mortgage_lender_url}
                    onChange={handleInputChange}
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Account Number
                  </label>
                  <input
                    type="text"
                    name="mortgage_account_number"
                    value={formData.mortgage_account_number}
                    onChange={handleInputChange}
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Current Balance
                  </label>
                  <input
                    type="number"
                    name="mortgage_balance"
                    value={formData.mortgage_balance}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Monthly Payment
                  </label>
                  <input
                    type="number"
                    name="mortgage_payment"
                    value={formData.mortgage_payment}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    name="mortgage_rate"
                    value={formData.mortgage_rate}
                    onChange={handleInputChange}
                    min="0"
                    step="0.001"
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Loan Start Date
                  </label>
                  <input
                    type="date"
                    name="mortgage_start_date"
                    value={formData.mortgage_start_date}
                    onChange={handleInputChange}
                    className={tw.input}
                  />
                </div>

                <div>
                  <label className={tw.label}>
                    Maturity Date
                  </label>
                  <input
                    type="date"
                    name="mortgage_maturity_date"
                    value={formData.mortgage_maturity_date}
                    onChange={handleInputChange}
                    className={tw.input}
                  />
                </div>

                {property?.latest_mortgage_statement_id && (
                  <div className="md:col-span-2">
                    <label className={tw.label}>
                      Latest Mortgage Statement
                    </label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => window.open(`/documents?id=${property.latest_mortgage_statement_id}`, '_blank')}
                        className="px-4 py-2 text-sm bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/25 font-mono transition-colors"
                      >
                        View Statement
                      </button>
                      <span className="text-sm text-gray-500 font-mono">
                        Document #{property.latest_mortgage_statement_id}
                      </span>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <div className="bg-gray-900/50 border border-gray-700/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 font-mono">
                      Tip: Email mortgage statements to <code className="bg-gray-800 px-1.5 py-0.5 rounded text-cyan-400 font-mono text-xs">4750-mortgage@gpume.com</code> for automatic processing
                    </p>
                  </div>
                </div>
              </>
            )}

            {formData.ownership_type === 'owned_outright' && property?.estimated_value && (
              <div className="md:col-span-2">
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-lg">
                  <p className="text-sm font-mono text-emerald-400">
                    Property owned outright - No mortgage
                  </p>
                  {property.estimated_value && (
                    <p className="text-sm text-emerald-500 font-mono mt-1">
                      Estimated Value: ${property.estimated_value.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* HOA Information */}
        <div className={tw.formWrap}>
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-6">HOA Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={tw.label}>
                HOA Website URL
              </label>
              <input
                type="url"
                name="hoa_url"
                value={formData.hoa_url}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                HOA Login URL
              </label>
              <input
                type="url"
                name="hoa_login_url"
                value={formData.hoa_login_url}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                HOA Username
              </label>
              <input
                type="text"
                name="hoa_username"
                value={formData.hoa_username}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                HOA Password
              </label>
              <input
                type="password"
                name="hoa_password"
                value={formData.hoa_password}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div className="md:col-span-2">
              <label className={tw.label}>
                HOA Notes
              </label>
              <textarea
                name="hoa_notes"
                value={formData.hoa_notes}
                onChange={handleInputChange}
                rows={3}
                className={tw.input}
              />
            </div>
          </div>
        </div>

        {/* Payment Schedule Information */}
        <div className={tw.formWrap}>
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-6">Payment Schedule Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={tw.label}>
                Payment Bank/Account
              </label>
              <input
                type="text"
                name="payment_bank_account"
                value={formData.payment_bank_account}
                onChange={handleInputChange}
                placeholder="e.g., US Bank Business-5982"
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Payment Frequency
              </label>
              <select
                name="payment_frequency"
                value={formData.payment_frequency}
                onChange={handleInputChange}
                className={tw.select}
              >
                <option value="">Select Frequency</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </select>
            </div>

            <div>
              <label className={tw.label}>
                Schedule Start Date
              </label>
              <input
                type="date"
                name="payment_schedule_start_date"
                value={formData.payment_schedule_start_date}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div>
              <label className={tw.label}>
                Next Payment Date
              </label>
              <input
                type="date"
                name="next_payment_date"
                value={formData.next_payment_date}
                onChange={handleInputChange}
                className={tw.input}
              />
            </div>

            <div className="md:col-span-2">
              <label className={tw.label}>
                Payment Details
              </label>
              <textarea
                name="payment_details"
                value={formData.payment_details}
                onChange={handleInputChange}
                placeholder="e.g., Assessments Due: Full amount"
                rows={3}
                className={tw.input}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
